'use client'
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc'
import { usePathname } from 'next/navigation'
import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { GoogleReviews } from '@/components/GoogleReviews'
import {
  fetchOrderedAdvisers,
  isAdviserDetailsBlok,
  orderedAdviserIdsFromBlok,
} from '@/lib/advisers/orderedAdvisers'
import { getStoryblokApi } from '@/lib/storyblok-api'

import AdviserDetails from '../../02-molecules/AdviserDetails/AdviserDetails'
import type { TabItem } from '../../02-molecules/TabGroup/Blok'
import { PhnxTabGroup } from '../../02-molecules/TabGroup/TabGroup'
import { AdvanceofficeProps, PhnxAdvanceoffice } from './Advanceoffice'
import styles from './Advanceoffice.module.css'

interface LocationBlock extends SbBlokData {
  component: 'location'
  latitude: string
  longitude: string
}

interface OfficeBlock extends SbBlokData {
  content: LocationBlock[]
}

interface AdvanceofficeBlokProps {
  blok: {
    title: AdvanceofficeProps['title']
    description: SbBlokData[]
    sponsors: any[]
    testimonials: any[]
    office: OfficeBlock[]
    tabContent: any
  } & SbBlokData
  uuid: string
  rels?: any
}

const Advanceoffice: FC<AdvanceofficeBlokProps> = ({ blok, uuid }) => {
  const pathname = usePathname()
  const [advisers, setAdvisers] = useState<any[]>([])
  const blokRef = useRef(blok)
  blokRef.current = blok

  const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'au'
  const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'
  const cmsAdviserOrderKey = JSON.stringify(orderedAdviserIdsFromBlok(blok))

  useEffect(() => {
    const loadAdvisers = async () => {
      const storyblokApi = getStoryblokApi()
      if (!storyblokApi) {
        console.error('Storyblok API client not initialized')
        return
      }
      try {
        const list = await fetchOrderedAdvisers(storyblokApi, {
          pathname: pathname || '',
          officeUuid: uuid,
          location,
          filterLocation,
          blok: blokRef.current,
        })
        setAdvisers(list)
      } catch (error) {
        console.error('Error fetching advisers:', error)
        setAdvisers([])
      }
    }

    loadAdvisers()
  }, [pathname, uuid, location, filterLocation, cmsAdviserOrderKey])

  const adviserRows = useMemo(
    () =>
      advisers.map((story: any) => ({
        uuid: story.uuid,
        title: story.name,
        role: story.content.title,
        link: `/adviser/${story.slug}`,
        avatarsrc: story.content.image.filename,
        avataralt: story.content.image.alt,
        biography: Array.isArray(story.content.bio)
          ? story.content.bio.map((child: SbBlokData) => (
              <StoryblokServerComponent blok={child} key={child._uid} />
            ))
          : null,
        specialties: story.content.specialities.map((speciality: any) => ({
          title: speciality,
          href: `/advisers/search?specialty=${speciality}`,
        })),
      })),
    [advisers]
  )

  const tabGroupTabs = useMemo((): TabItem[] => {
    if (!blok.tabContent) return []
    return blok.tabContent.map((item: any) => ({
      name: item.tabName,
      content:
        item.tabContent?.length > 0 && isAdviserDetailsBlok(item.tabContent[0])
          ? adviserRows.map((row) => (
              <AdviserDetails
                key={row.uuid}
                avatar={{ src: row.avatarsrc, alt: row.avataralt }}
                name={row.title}
                title={row.title}
                role={row.role}
                link={row.link}
                specialties={row.specialties}
                biography={row.biography}
              />
            ))
          : item.tabContent.map((nestedBlok: SbBlokData) => (
              <>
                {blok.office?.map((officeBlok: SbBlokData) => (
                  <div className={styles['mob-office-details']} key={officeBlok._uid}>
                    <StoryblokServerComponent blok={{ ...officeBlok }} />
                  </div>
                ))}
                <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
                {blok.testimonials?.map((nestedBlok: SbBlokData) => (
                  <div className={styles['mob-testimonies']} key={nestedBlok._uid}>
                    <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
                  </div>
                ))}
                {blok.sponsors?.map((nestedBlok: SbBlokData) => (
                  <div className={styles['mob-office-details']} key={nestedBlok._uid}>
                    <StoryblokServerComponent blok={nestedBlok} />
                  </div>
                ))}
              </>
            )),
    }))
  }, [blok.tabContent, blok.office, blok.testimonials, blok.sponsors, adviserRows])

  return (
    <div {...storyblokEditable(blok)}>
      <PhnxAdvanceoffice
        title={blok.title}
        description={blok.description?.map((nestedBlok: SbBlokData) => (
          <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
        ))}
        tabContent={<PhnxTabGroup tabs={tabGroupTabs} />}
        testimonials={blok.testimonials?.map((review: any) => (
          <StoryblokServerComponent blok={review} key={review._uid} />
        ))}
        sponsors={blok.sponsors?.map((nestedBlok: SbBlokData) => (
          <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
        ))}
        office={blok.office?.map((nestedBlok: OfficeBlock) => (
          <StoryblokServerComponent
            blok={{ ...nestedBlok, title: blok.title }}
            key={nestedBlok._uid}
          />
        ))}
        reviews={
          blok.office[0]?.placeId ? (
            <GoogleReviews placeId={String(blok.office[0].placeId)} />
          ) : null
        }
      />
    </div>
  )
}

export default Advanceoffice
